import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useDisclosure,
  useToast,
  HStack,
  Box,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import {
  apiGetMe,
  apiGetMySubscription,
  apiGetMyPayments,
  apiGetPlans,
  apiUpdateMe,
  apiCreateCheckoutSession,
  apiCancelMySubscription,
  ApiErrorResponse,
  AuthUser,
  getStoredAuth,
  MemberPaymentDetails,
  MemberSubscriptionDetails,
  SubscriptionPlan,
} from '../api/client';
import PortalPageShell from './PortalPageShell';

function isPlanActive(plan: SubscriptionPlan) {
  return plan.is_active === true || plan.is_active === 1;
}

export default function MemberDashboard() {
  const { user: storedUser } = getStoredAuth();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [user, setUser] = useState<AuthUser | null>(storedUser);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fullName, setFullName] = useState(storedUser?.full_name || '');
  const [phone, setPhone] = useState(storedUser?.phone || '');
  const [password, setPassword] = useState('');
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [subscription, setSubscription] = useState<MemberSubscriptionDetails | null>(null);
  const [latestPayment, setLatestPayment] = useState<MemberPaymentDetails | null>(null);
  const [subscriptionError, setSubscriptionError] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [userPayments, setUserPayments] = useState<MemberPaymentDetails[]>([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [paymentsError, setPaymentsError] = useState('');
  const [isPaymentsModalOpen, setIsPaymentsModalOpen] = useState(false);

  // Helper to shorten long identifiers for display (keep front and back for recognizability)
  const formatShortId = (value?: string | null) => {
    if (!value) return '-';
    const v = String(value).trim();
    if (v.length <= 20) return v;
    return `${v.slice(0, 8)}…${v.slice(-6)}`;
  };

  useEffect(() => {
    let active = true;

    const loadProfile = async () => {
      setLoadingProfile(true);

      try {
        const { token } = getStoredAuth();

        if (!token) {
          navigate('/member/login', { replace: true });
          return;
        }

        const result: any = await apiGetMe();

        if (!active) return;

        if (result?.status === 401 || result?.status === 403) {
          navigate('/member/login', { replace: true });
          return;
        }

        const profileUser = result?.user && typeof result.user === 'object'
          ? result.user
          : result;

        if (profileUser && typeof profileUser === 'object' && profileUser.email) {
          setUser(profileUser);
          setFullName(profileUser.full_name || '');
          setPhone(profileUser.phone || '');
        }
      } catch {
        if (!active) return;
        navigate('/member/login', { replace: true });
      } finally {
        if (active) {
          setLoadingProfile(false);
        }
      }
    };

    loadProfile();

    return () => {
      active = false;
    };
  }, [navigate]);

  useEffect(() => {
    let active = true;

    const loadSubscription = async () => {
      setLoadingSubscription(true);
      setSubscriptionError('');

      try {
        const result = await apiGetMySubscription();

        if (!active) return;

        if (result.status === 401 || result.status === 403) {
          navigate('/member/login', { replace: true });
          return;
        }

        if (result.success) {
          setSubscription(result.subscription);
          setLatestPayment(result.latest_payment);
        } else {
          setSubscription(null);
          setLatestPayment(null);
          setSubscriptionError(result.message || 'Unable to load subscription details right now.');
        }
      } finally {
        if (active) {
          setLoadingSubscription(false);
        }
      }
    };

    loadSubscription();

    return () => {
      active = false;
    };
  }, [navigate]);

  useEffect(() => {
    let active = true;

    const loadPlans = async () => {
      setLoadingPlans(true);

      try {
        if (subscription) {
          setPlans([]);
          setSelectedPlanId(null);
          return;
        }

        const result: SubscriptionPlan[] | ApiErrorResponse = await apiGetPlans();

        if (!active) return;

        if (Array.isArray(result)) {
          const activePlans = result.filter(isPlanActive);
          setPlans(activePlans);
          setSelectedPlanId(current => current ?? activePlans[0]?.id ?? null);
        } else {
          setPlans([]);
        }
      } finally {
        if (active) {
          setLoadingPlans(false);
        }
      }
    };

    loadPlans();

    return () => {
      active = false;
    };
  }, [subscription]);

  useEffect(() => {
    if (!isOpen) return;
    setFullName(user?.full_name || '');
    setPhone(user?.phone || '');
    setPassword('');
  }, [isOpen, user]);

  const formatDate = (value?: string | null) => {
    if (!value) return '-';
    const date = new Date(value.replace(' ', 'T'));
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
    }).format(date);
  };

  const handleSave = async () => {
    const trimmedPassword = password.trim();

    if (trimmedPassword && trimmedPassword.length < 6) {
      toast({
        title: 'Invalid password',
        description: 'Password must be at least 6 characters.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    try {
      setSaving(true);

      const result: any = await apiUpdateMe({
        full_name: fullName.trim() || undefined,
        phone,
        password: trimmedPassword || undefined,
      });

      if (result?.success && result?.user) {
        setUser(prev => ({
          ...prev,
          ...result.user,
        }));
        setFullName(result.user.full_name || '');
        setPassword('');
        onClose();
        toast({
          title: 'Profile updated',
          description: result?.message || 'Your details have been updated successfully.',
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
        return;
      }

      if (result?.status === 401 || result?.status === 403) {
        navigate('/member/login', { replace: true });
        return;
      }

      toast({
        title: 'Update failed',
        description: result?.message || 'Unable to update your details right now.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: 'Update failed',
        description: error?.message || 'Unable to update your details right now.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const joinedOn = user?.joined_at || user?.created_at || null;

  const selectedPlan = plans.find(plan => plan.id === selectedPlanId) || null;

  const handleChoosePlan = async () => {
    if (!selectedPlan) {
      toast({
        title: 'Select a plan',
        description: 'Choose a subscription plan to continue.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Show loading toast and start checkout process
    setCheckoutLoading(true);
    const toastId = toast({
      title: 'Processing',
      description: 'Redirecting you to checkout. Please wait...',
      status: 'loading',
      duration: 9000,
      isClosable: false,
    });

    try {
      const result = await apiCreateCheckoutSession(selectedPlan.id);

      if (result.success && result.checkout_url) {
        toast.close(toastId);
        toast({
          title: 'Redirecting to Stripe',
          description: 'Setting up your payment...',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });

        // Redirect to the Stripe checkout URL
        window.location.href = result.checkout_url;
      } else {
        toast.close(toastId);
        toast({
          title: 'Checkout Error',
          description: result.message || 'Unable to initiate payment. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error: any) {
      toast.close(toastId);
      toast({
        title: 'Error',
        description: error?.message || 'Unable to initiate payment. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCheckoutLoading(false);
    }
  };

  const loadUserPayments = async () => {
    setLoadingPayments(true);
    setPaymentsError('');

    try {
      const result = await apiGetMyPayments();

      if (Array.isArray(result)) {
        setUserPayments(result);
      } else {
        setUserPayments([]);
        setPaymentsError(result?.message || 'Unable to load your payments right now.');
      }
    } catch (error: any) {
      setUserPayments([]);
      setPaymentsError(error?.message || 'Unable to load your payments right now.');
    } finally {
      setLoadingPayments(false);
    }
  };

  const openPaymentsModal = async () => {
    await loadUserPayments();
    setIsPaymentsModalOpen(true);
  };

  const closePaymentsModal = () => {
    setIsPaymentsModalOpen(false);
  };


  if (loadingProfile) {
    return (
      <PortalPageShell title="Subscription" subtitle="View your account and subscription details.">
        <Stack align="center" py={12} spacing={4}>
          <Spinner size="xl" color="blue.500" />
          <Text color="gray.600">Loading your details...</Text>
        </Stack>
      </PortalPageShell>
    );
  }

  return (
    <PortalPageShell
      title="Subscription"
      subtitle="View your account and subscription details."
    >
      <Stack spacing={6}>
        <Stack spacing={4} p={5} borderWidth="1px" borderColor="gray.200" borderRadius="xl" bg="gray.50">
          <HStack justify="space-between" align="center" flexWrap="wrap">
            <Heading size="sm">Basic Details</Heading>
            <Button size="sm" variant="outline" colorScheme="blue" onClick={onOpen}>
              Edit
            </Button>
          </HStack>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <Text><strong>Full Name:</strong> {user?.full_name || '-'}</Text>
            <Text><strong>Email:</strong> {user?.email || 'Not available'}</Text>
            <Text>
              <strong>Role:</strong>{' '}
              <Badge colorScheme={user?.role === 'admin' ? 'purple' : 'green'}>{user?.role || 'user'}</Badge>
            </Text>
            <Text><strong>Phone:</strong> {user?.phone || '-'}</Text>
            <Text><strong>Joined On:</strong> {formatDate(joinedOn)}</Text>
          </SimpleGrid>
        </Stack>

        <Stack spacing={4} p={5} borderWidth="1px" borderColor="gray.200" borderRadius="xl" bg="white">
          {loadingSubscription ? (
            <Stack align="center" py={6} spacing={3}>
              <Spinner color="blue.500" />
              <Text color="gray.600">Loading subscription details...</Text>
            </Stack>
          ) : subscription ? (
            <Stack spacing={5}>
              <Heading size="sm">Current Subscription</Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <Text><strong>Plan:</strong> {subscription.plan_name}</Text>
                <Text><strong>Status:</strong> {subscription.status || '-'}</Text>
                <Text><strong>Renewal Status:</strong> {subscription.renewal_status || '-'}</Text>
                <Text><strong>Amount:</strong> {subscription.currency} {Number(subscription.amount).toFixed(2)}</Text>
                <Text><strong>Billing Cycle:</strong> {subscription.billing_cycle}</Text>
                <Text><strong>Starts At:</strong> {formatDate(subscription.starts_at)}</Text>
                <Text><strong>Expires At:</strong> {formatDate(subscription.expires_at)}</Text>
                <Text><strong>Duration:</strong> {subscription.duration_days} days</Text>
                <Text><strong>Days Left:</strong> {subscription.days_left ?? '-'}</Text>
                <Text><strong>Will Renew:</strong> {subscription.will_renew === true ? 'Yes' : subscription.will_renew === false ? 'No' : '-'}</Text>
              </SimpleGrid>

              {subscription.plan_description ? (
                <Text color="gray.600">{subscription.plan_description}</Text>
              ) : null}

              <Stack spacing={4} p={5} borderWidth="1px" borderColor="gray.200" borderRadius="xl" bg="gray.50">
                <Heading size="sm">Latest Payment</Heading>
                {latestPayment ? (
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <Text><strong>Gateway:</strong> {latestPayment.gateway || '-'}</Text>
                    <Text><strong>Reference:</strong> {formatShortId(latestPayment.payment_reference)}</Text>
                    <Text><strong>Amount:</strong> {latestPayment.currency} {Number(latestPayment.amount).toFixed(2)}</Text>
                    <Text><strong>Status:</strong> {latestPayment.status || '-'}</Text>
                    <Text><strong>Paid At:</strong> {formatDate(latestPayment.paid_at)}</Text>
                  </SimpleGrid>
                ) : (
                  <Text color="gray.600">No payment details available.</Text>
                )}

                <HStack justify="flex-end">
                    <Button size="sm" colorScheme="blue" variant="outline" onClick={openPaymentsModal} isLoading={loadingPayments}>
                      View all payments
                    </Button>
                    {/* Cancel subscription button: only show when subscription is active and will_renew is true */}
                    {subscription.is_active !== false && subscription.will_renew !== false ? (
                      <Button size="sm" colorScheme="red" onClick={async () => {
                        const ok = window.confirm('Are you sure you want to cancel your subscription at period end?');
                        if (!ok) return;
                        try {
                          setCancelling(true);
                          const res = await apiCancelMySubscription();
                          if (res.success) {
                            toast({ title: 'Subscription cancelled', description: res.message || 'Your subscription will not renew at period end.', status: 'success', duration: 5000, isClosable: true });
                            // reload subscription
                            const fresh = await apiGetMySubscription();
                            if (fresh.success) setSubscription(fresh.subscription);
                          } else {
                            toast({ title: 'Cancellation failed', description: res.message || 'Unable to cancel your subscription right now.', status: 'error', duration: 5000, isClosable: true });
                          }
                        } catch (error: any) {
                          toast({ title: 'Cancellation error', description: error?.message || 'Unable to cancel your subscription right now.', status: 'error', duration: 5000, isClosable: true });
                        } finally {
                          setCancelling(false);
                        }
                      }} isLoading={cancelling}>
                        Cancel Subscription
                      </Button>
                    ) : null}
                </HStack>

                {paymentsError ? (
                  <Alert status="warning" borderRadius="lg">
                    <AlertIcon />
                    {paymentsError}
                  </Alert>
                ) : null}
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={5}>
              <Heading size="sm">Select a Subscription</Heading>
              <Text color="gray.600">
                Choose one of the active subscription plans below. For now, you can also skip this step for testing.
              </Text>

              {subscriptionError ? (
                <Alert status="warning" borderRadius="lg">
                  <AlertIcon />
                  {subscriptionError}
                </Alert>
              ) : null}

              {loadingPlans ? (
                <Stack align="center" py={6} spacing={3}>
                  <Spinner color="blue.500" />
                  <Text color="gray.600">Loading active plans...</Text>
                </Stack>
              ) : plans.length > 0 ? (
                <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                  {plans.map(plan => {
                    const isSelected = plan.id === selectedPlanId;

                    return (
                      <Box
                        key={plan.id}
                        p={5}
                        borderWidth="1px"
                        borderColor={isSelected ? 'blue.400' : 'gray.200'}
                        borderRadius="xl"
                        bg={isSelected ? 'blue.50' : 'gray.50'}
                        cursor="pointer"
                        onClick={() => setSelectedPlanId(plan.id)}
                      >
                        <Stack spacing={3}>
                          <HStack justify="space-between" align="flex-start">
                            <Heading size="sm">{plan.name}</Heading>
                            {isSelected ? <Badge colorScheme="blue">Selected</Badge> : null}
                          </HStack>
                          <Text color="gray.600">{plan.description || plan.billing_summary || 'Subscription plan'}</Text>
                          <Text fontWeight="700" color="gray.800">
                            {plan.currency || 'AUD'} {Number(plan.amount).toFixed(2)}
                          </Text>
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                            <Text><strong>Cycle:</strong> {plan.billing_cycle}</Text>
                            <Text><strong>Duration:</strong> {plan.duration_days} days</Text>
                          </SimpleGrid>
                        </Stack>
                      </Box>
                    );
                  })}
                </SimpleGrid>
              ) : (
                <Text color="gray.600">No active subscription plans are available right now.</Text>
              )}

              <HStack spacing={3} pt={2} flexWrap="wrap">
                <Button 
                  colorScheme="blue" 
                  onClick={handleChoosePlan} 
                  isLoading={checkoutLoading}
                  isDisabled={loadingPlans || plans.length === 0 || !selectedPlan || checkoutLoading}
                >
                  Continue With Selected Plan
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/member/articles')}
                  isDisabled={checkoutLoading}
                >
                  Skip for now
                </Button>
              </HStack>
            </Stack>
          )}
        </Stack>
      </Stack>

      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
        <ModalOverlay bg="blackAlpha.500" />
        <ModalContent>
          <ModalHeader>Edit Basic Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={5}>
              <Text color="gray.600">
                Update your full name, phone number, or password here. Email remains read only.
              </Text>

              <FormControl>
                <FormLabel>Full Name</FormLabel>
                <Input
                  type="text"
                  placeholder="Enter your full name"
                  value={fullName}
                  onChange={e => setFullName(e.target.value)}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Email</FormLabel>
                <Input value={user?.email || ''} isReadOnly bg="gray.50" />
                <FormHelperText>Email is read only.</FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Phone</FormLabel>
                <Input
                  type="tel"
                  placeholder="Enter your phone number"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                />
              </FormControl>

              <FormControl>
                <FormLabel>New Password</FormLabel>
                <Input
                  type="password"
                  placeholder="Enter a new password"
                  autoComplete="new-password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                />
                <FormHelperText>Leave blank if you do not want to change your password.</FormHelperText>
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button variant="ghost" onClick={onClose}>
                Close
              </Button>
              <Button colorScheme="blue" onClick={handleSave} isLoading={saving}>
                Save Changes
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isPaymentsModalOpen} onClose={closePaymentsModal} size="xl" isCentered>
        <ModalOverlay bg="blackAlpha.500" />
        <ModalContent>
          <ModalHeader>All Payments</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              {loadingPayments ? (
                <Stack align="center" py={4} spacing={2}>
                  <Spinner />
                  <Text>Loading payment history...</Text>
                </Stack>
              ) : paymentsError ? (
                <Alert status="warning" borderRadius="lg">
                  <AlertIcon />
                  {paymentsError}
                </Alert>
              ) : userPayments.length === 0 ? (
                <Text color="gray.600">No past payments found.</Text>
              ) : (
                <Stack spacing={3}>
                  {userPayments.map((payment) => (
                    <Box key={payment.id} p={3} borderWidth="1px" borderRadius="md" bg="gray.50">
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                        <Text><strong>Amount:</strong> {payment.currency} {Number(payment.amount).toFixed(2)}</Text>
                        <Text><strong>Status:</strong> {payment.status || '-'}</Text>
                        <Text><strong>Subscription Start:</strong> {formatDate(payment.sub_starts_at)}</Text>
                        <Text><strong>Subscription Expires:</strong> {formatDate(payment.sub_expires_at)}</Text>
                        <Text><strong>Sub Status:</strong> {payment.sub_status || '-'}</Text>
                        <Text><strong>Plan Name:</strong> {payment.plan_name || '-'}</Text>
                        <Text><strong>Plan Description:</strong> {payment.plan_description || '-'}</Text>
                        <Text><strong>Billing Cycle:</strong> {payment.billing_cycle || '-'}</Text>
                        <Text><strong>Plan Duration:</strong> {payment.plan_duration_days ?? '-'} days</Text>
                        <Text><strong>Plan Amount:</strong> {payment.plan_currency || '-'} {payment.plan_amount ?? '-'}</Text>
                        <Text><strong>Gateway:</strong> {payment.gateway || '-'}</Text>
                        <Text><strong>Paid at:</strong> {formatDate(payment.paid_at)}</Text>
                      </SimpleGrid>
                    </Box>
                  ))}
                </Stack>
              )}
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={closePaymentsModal}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PortalPageShell>
  );
}
